import { apiCall, API_CONFIG } from './api';
import { getWebDetails } from '../utils/utils';

export const authAPI = {
  login: async (email, password) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.ADMIN_LOGIN, {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          webDetails: getWebDetails(),
          fcmToken: '',
        }),
      });

      if (response.success) {
        localStorage.setItem('adminToken', response.data.accessToken);
        localStorage.setItem('adminData', JSON.stringify(response.data));
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: response.message || response.msg || response.error || 'Login failed',
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
  },

  handleSessionExpired: () => {
    authAPI.logout();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  },

  getToken: () => {
    return localStorage.getItem('adminToken');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('adminToken');
  },

  getAdminData: () => {
    const adminData = localStorage.getItem('adminData');
    return adminData ? JSON.parse(adminData) : null;
  },

  verify: async () => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.ADMIN_VERIFY, {
        method: 'GET',
      });

      if (response.success) {
        const existingData = authAPI.getAdminData();
        const updatedData = {
          ...existingData,
          ...response.data,
          permissions: response.data.permissions ?? existingData?.permissions,
          currentStore: response.data.currentStore ?? existingData?.currentStore,
          stores: response.data.stores ?? existingData?.stores,
        };
        localStorage.setItem('adminData', JSON.stringify(updatedData));
        return { success: true, data: updatedData };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Verification failed',
      };
    } catch (error) {
      console.error('Verify error:', error);
      return {
        success: false,
        error: 'Verification failed. Please try again.',
      };
    }
  },

  switchStore: async (storeId) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.ADMIN_SWITCH_STORE, {
        method: 'POST',
        body: JSON.stringify({ storeId }),
      });

      if (response.success) {
        localStorage.setItem('adminToken', response.data.accessToken);
        const existingData = authAPI.getAdminData();
        const updatedData = {
          ...existingData,
          ...response.data,
          currentStore: response.data.currentStore,
          stores: response.data.stores,
          accessToken: response.data.accessToken,
        };
        localStorage.setItem('adminData', JSON.stringify(updatedData));
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to switch store',
      };
    } catch (error) {
      console.error('Switch store error:', error);
      return {
        success: false,
        error: 'Failed to switch store. Please try again.',
      };
    }
  },

  createAdmin: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.ADMIN_CREATE, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || response.error || 'Failed to create admin',
      };
    } catch (error) {
      console.error('Create admin error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  },

  getAdminsList: async (page = 1, limit = 20) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ADMIN_LIST}?${queryParams.toString()}`,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
          pagination: response.pagination,
          msg: response.msg,
        };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to fetch admins',
      };
    } catch (error) {
      console.error('List admins error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  },

  updateAdmin: async (id, payload) => {
    try {
      const hasFile = payload.profilePictureFile instanceof File;
      let requestBody;

      if (!hasFile) {
        requestBody = { ...payload };
        delete requestBody.profilePictureFile;
        delete requestBody.profilePicture;
        delete requestBody.appAlerts;
      } else {
        requestBody = new FormData();
        Object.keys(payload).forEach((key) => {
          if (key === 'profilePictureFile') {
            requestBody.append('profilePicture', payload[key]);
          } else if (key === 'webAlerts') {
            requestBody.append(key, JSON.stringify(payload[key]));
          } else if (key !== 'profilePicture' && key !== 'appAlerts') {
            requestBody.append(key, Array.isArray(payload[key]) ? JSON.stringify(payload[key]) : payload[key]);
          }
        });
      }

      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ADMIN_UPDATE}/${id}`, {
        method: 'PUT',
        body: requestBody instanceof FormData ? requestBody : JSON.stringify(requestBody),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || response.error || 'Failed to update admin',
      };
    } catch (error) {
      console.error('Update admin error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  },

  deleteAdmin: async (id) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ADMIN_DELETE}/${id}`, {
        method: 'DELETE',
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || response.error || 'Failed to delete admin',
      };
    } catch (error) {
      console.error('Delete admin error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.',
      };
    }
  },


};
